import React, { useContext, useState } from "react";
import {
  AlertTitle,
  AppBar,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Hidden,
  IconButton,
  Snackbar,
  Toolbar,
  Typography,
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import UserLoggedInButton from "./common/notify/UserLoggedInButton";
import { Alert } from "@mui/material";
import { UserContext } from "./context/UserContext";
import {
  isPOSMode,
  isKDSMode,
  getPosInactivityTimeoutSeconds,
  POS_IDLE_WARNING_SECONDS,
} from "./common/util";
import Notifications from "./common/notify/Notifications";
import Lookup from "./common/search/Lookup";
import LiveHelpOutlinedIcon from "@mui/icons-material/LiveHelpOutlined";
import { Link } from "react-router-dom";
import { BAKER_PIC_FOLDER, bakeStreetLogo } from "./common/constants";
import { Link as RouterLink } from "react-router-dom";
import AccessCashDrawerButton from "./order/component/AccessCashDrawerButton";
import { auth } from "./firebase";
import bkstApi from "./api";
import usePosIdleLogoff from "./common/usePosIdleLogoff";

function idleCountdownColor(seconds) {
  if (seconds > 10) return "info.main";
  if (seconds > 5) return "warning.main";
  return "error.main";
}

function Header(props) {
  const { user, setUser, toast, setToast } = useContext(UserContext);
  const [endingImpersonation, setEndingImpersonation] = useState(false);
  const idleCountdown = usePosIdleLogoff();
  const showIdleSlot = isPOSMode(user) && getPosInactivityTimeoutSeconds(user) > 0;
  const countdownColor = idleCountdown != null ? idleCountdownColor(idleCountdown) : undefined;

  const endImpersonation = () => {
    setEndingImpersonation(true);
    bkstApi
      .post("/my/end-impersonation")
      .catch((err) => {
        console.log("end-impersonation failed", err);
      })
      .finally(() => {
        auth
          .signOut()
          .then(() => {
            setUser("");
            window.location.href = "/";
          })
          .catch(() => {
            setEndingImpersonation(false);
          });
      });
  };

  // always show hamburgerMenu to Kitchen
  const hamburgerMenu = (
    <IconButton
      aria-label="open drawer"
      edge="start"
      onClick={props.toggleMobileDrawer}
      size="large"
    >
      <MenuRoundedIcon />
    </IconButton>
  );

  return (
    <Box
      sx={
        user?.alert || user?.impersonating
          ? {
              position: "sticky",
              top: 0,
              zIndex: (theme) => theme.zIndex.appBar + 1,
            }
          : undefined
      }
    >
      {user?.impersonating && (
        <Alert
          severity="warning"
          icon={false}
          sx={{
            alignItems: "center",
            borderRadius: 0,
            py: 0.5,
          }}
          action={
            <Button
              color="inherit"
              size="small"
              disabled={endingImpersonation}
              onClick={endImpersonation}
            >
              End session
            </Button>
          }
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              alt={user.profile?.bizName || "Bakery"}
              src={`${BAKER_PIC_FOLDER}/${user.acctId}/logo400.webp`}
              sx={{ width: 32, height: 32 }}
            />
            {user.impersonatedBy && (
              <img
                src={`https://storage.googleapis.com/bakestreet-f55a0.appspot.com/img/u/${user.impersonatedBy}.webp`}
                style={{ width: 32, height: 32 }}
              />
            )}
            <Typography variant="body2">
              Impersonating as {user.role}
              {user.profile?.bizName ? ` · ${user.profile.bizName}` : ""}
            </Typography>
          </Box>
        </Alert>
      )}
      {user && (
        <AppBar
          position="static"
          style={{ background: "#fff", boxShadow: "none", borderBottom: "1px solid #eee" }}
        >
          <Toolbar
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {isPOSMode(user) || isKDSMode(user) ? (
                hamburgerMenu
              ) : (
                <Hidden lgUp>{hamburgerMenu}</Hidden>
              )}
              {(isPOSMode(user) || isKDSMode(user)) && (
                <Link
                  component={RouterLink}
                  to={isKDSMode(user) ? "/production/tracker" : "/"}
                >
                  <img alt="logo" style={{ width: "110px" }} src={bakeStreetLogo}></img>
                </Link>
              )}
            </Box>
            <Box
              style={{
                display: "flex",
                justifyContent: "flex-end",
                flexGrow: "1",
                alignItems: "center",
                gap: "5px",
              }}
            >
              {showIdleSlot && (
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    position: "relative",
                    // Paint above full-screen dialogs so the countdown stays visible
                    zIndex: (theme) => theme.zIndex.modal + 1,
                    pointerEvents: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {idleCountdown != null && (
                    <>
                      <CircularProgress
                        variant="determinate"
                        value={100}
                        size={36}
                        thickness={4}
                        sx={{ color: countdownColor, opacity: 0.2, position: "absolute" }}
                      />
                      <CircularProgress
                        variant="determinate"
                        value={
                          (Math.min(idleCountdown, POS_IDLE_WARNING_SECONDS) /
                            POS_IDLE_WARNING_SECONDS) *
                          100
                        }
                        size={36}
                        thickness={4}
                        sx={{ color: countdownColor }}
                      />
                      <Box
                        sx={{
                          top: 0,
                          left: 0,
                          bottom: 0,
                          right: 0,
                          position: "absolute",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, color: countdownColor, lineHeight: 1 }}
                        >
                          {idleCountdown}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              )}
              <IconButton
                onClick={() => {
                  window.$zoho.salesiq.visitor.name(
                    `${user.profile?.bizName} ${user.firstName ? `: ${user.firstName} ${user.lastName || ""}` : ""}`
                  );
                  window.$zoho.salesiq.visitor.info({ appVersion: process.env.REACT_APP_VERSION });
                  window.$zoho.salesiq.chat.waittime(600);
                  window.$zoho.salesiq.floatwindow.visible("show");
                }}
              >
                <LiveHelpOutlinedIcon />
              </IconButton>
              {isPOSMode(user) && (
                <>
                  {user?.config?.receiptPrinterId && <AccessCashDrawerButton />}
                  <Button component={RouterLink} to={"/today"} variant="outlined" color="primary">
                    TODAY
                  </Button>
                </>
              )}
              <Lookup mode={isPOSMode(user) ? "collapsed" : ""} />
              {!isKDSMode(user) && <Notifications />}
              <UserLoggedInButton user={user} />
            </Box>
          </Toolbar>
        </AppBar>
      )}
      {user && user.alert && (
        <Box sx={{ backgroundColor: "#f5f5f5" }}>
          <Alert severity="error">
            <AlertTitle>{user.alert.title}</AlertTitle>
            {user.alert.description}
          </Alert>
          <Button
            fullWidth
            variant="contained"
            color="error"
            onClick={() => (window.location.href = user.alert.link)}
          >
            {user.alert.cta}
          </Button>
        </Box>
      )}
      <Snackbar
        open={Boolean(toast)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={5000}
        onClose={() => {
          setToast("");
        }}
      >
        <Alert
          variant="filled"
          severity={toast.severity}
          onClose={() => {
            setToast("");
          }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
export default Header;
